import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { SadieSimulatorTapbackMenu } from "@/components/tools/sadie-simulator/SadieSimulatorTapbackMenu";

import { SadieSimulatorMessage, SadieSimulatorTapback } from "@/types";

// Sadie's tapbacks, the way a phone draws them.
const SADIE_TAPBACK_EMOJI: Record<string, string> = {
  love: "❤️",
  like: "👍",
  laugh: "😂",
  emphasize: "‼️",
  joy: "😂",
  raised_hands: "🙌",
  fire: "🔥",
  muscle: "💪",
  pray: "🙏",
  "100": "💯",
  saluting_face: "🫡",
};

// Sadie reads as plain text on the page. The tester reads as a sent bubble on
// the right, the way a chat app draws the person typing.
export const SadieSimulatorMessageBubble = ({
  canTapBack,
  message,
  onTapback,
}: {
  canTapBack: boolean;
  message: SadieSimulatorMessage;
  onTapback: (quoId: string, reaction: SadieSimulatorTapback) => void;
}) => {
  /* Render */
  const render = () => {
    if (message.isTapback) {
      return (
        <Text className="self-end text-xs font-normal italic text-slate-400">
          {message.body}
        </Text>
      );
    }
    if (message.from === "WORKER") {
      return (
        <Column className="relative max-w-[80%] self-end">
          {message.tapback ? (
            <Text className="absolute -left-3 -top-3 rounded-full border border-slate-200 bg-white px-1.5 py-0.5 text-sm">
              {SADIE_TAPBACK_EMOJI[message.tapback] || message.tapback}
            </Text>
          ) : null}
          <Text className="whitespace-pre-wrap rounded-2xl bg-slate-100 px-4 py-2.5 text-[15px] font-normal leading-relaxed text-slate-900">
            {message.body}
          </Text>
          <MessageMedia message={message} />
        </Column>
      );
    }
    return (
      <Row className="group w-full items-start gap-2">
        <Column className="min-w-0 flex-1 gap-1">
          <Text className="whitespace-pre-wrap text-[15px] font-normal leading-relaxed text-slate-900">
            {message.body}
          </Text>
          {message.effect ? (
            <Text className="text-xs font-normal text-slate-400">
              {`Sent with ${message.effect}`}
            </Text>
          ) : null}
          <MessageMedia message={message} />
        </Column>
        {canTapBack ? (
          <SadieSimulatorTapbackMenu
            onTapback={(reaction) => {
              onTapback(message.id, reaction);
            }}
          />
        ) : null}
      </Row>
    );
  };

  return render();
};

// A file Sadie sent, like an application's screenshot, by what it shows.
const mediaLabel = (item: SadieSimulatorMessage["media"][number]) =>
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  item.description || item.type;

const MessageMedia = ({ message }: { message: SadieSimulatorMessage }) => (
  <>
    {message.media.map((item) => (
      <a
        className="text-xs text-slate-500 underline"
        href={item.url}
        key={item.url}
        rel="noreferrer"
        target="_blank"
      >
        {mediaLabel(item)}
      </a>
    ))}
  </>
);
